<app-ui-page-header title="Perfil" subtitle="Seus dados pessoais e preferências de conta." />

<div class="mt-6 grid gap-5">
  <section
    appUiPanelCard
    class="profile-banner surface-noise relative isolate overflow-hidden !p-2 md:!p-3"
    [formGroup]="profileForm"
  >
    <div class="relative z-10 grid gap-3 md:grid-cols-[5rem_minmax(0,1fr)] md:items-center">
      <input
        #avatarInput
        appUiFileInput
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        (change)="uploadAvatar($event)"
      />

      <button
        appUiAvatarButton
        type="button"
        (click)="avatarInput.click()"
        [disabled]="uploadingAvatar()"
        aria-label="Alterar avatar"
      >
        @if (profileAvatarUrl()) {
          <img class="size-full object-cover" [src]="profileAvatarUrl()" alt="" />
        } @else {
          <span class="grid size-16 place-items-center rounded-lg border border-line bg-soft text-2xl font-semibold text-muted">
            {{ profileInitials() }}
          </span>
        }

        <span
          class="absolute inset-0 grid place-items-center bg-ink/42 text-panel opacity-0 transition group-hover/avatar:opacity-100 group-focus-visible/avatar:opacity-100"
        >
          <span class="inline-flex items-center gap-2 rounded-md border border-panel/40 bg-panel/16 px-3 py-2 text-sm font-semibold backdrop-blur-sm">
            <ng-icon name="heroPencil" size="16" aria-hidden="true" />
            {{ uploadingAvatar() ? 'Enviando' : 'Alterar' }}
          </span>
        </span>
      </button>

      <div class="grid min-w-0 gap-2">
        <div class="min-w-0">
          @if (editingName()) {
            <div class="grid min-w-0 grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2">
              <input appInput formControlName="displayName" autocomplete="name" aria-label="Nome" />
              <button
                appButton
                type="button"
                variant="primary"
                size="sm"
                (click)="saveName()"
                [disabled]="saving() || profileForm.controls.displayName.invalid"
              >
                <ng-icon name="heroCheck" size="15" aria-hidden="true" />
                Salvar
              </button>
              <button appButton type="button" variant="ghost" size="sm" (click)="cancelEditingName()" aria-label="Cancelar edição do nome">
                <ng-icon name="heroXMark" size="15" aria-hidden="true" />
              </button>
            </div>
          } @else {
            <div class="group/name flex min-w-0 items-center gap-2">
              <h2 class="truncate text-xl font-semibold leading-tight text-ink">{{ profileName() }}</h2>
              <button
                appButton
                type="button"
                variant="secondary"
                size="sm"
                class="opacity-0 transition group-hover/name:opacity-100 focus-visible:opacity-100"
                (click)="startEditingName()"
                aria-label="Editar nome"
                title="Editar nome"
              >
                <ng-icon name="heroPencil" size="15" aria-hidden="true" />
              </button>
            </div>
          }

          <p class="mt-0.5 truncate text-sm font-medium leading-tight text-muted">{{ profileEmail() }}</p>
        </div>
      </div>
    </div>
  </section>

  <section appUiPanelCard class="flex flex-wrap items-center justify-between gap-3">
    <div class="min-w-0">
      <p class="text-sm font-semibold text-ink">Senha</p>
      <p appUiHint class="mt-1">Enviaremos as instruções para o e-mail principal da conta.</p>
    </div>
    <button
      appButton
      type="button"
      variant="secondary"
      (click)="requestPasswordReset()"
      [disabled]="requestingPasswordReset()"
    >
      <ng-icon name="heroKey" size="16" aria-hidden="true" />
      {{ requestingPasswordReset() ? 'Enviando' : 'Redefinir senha' }}
    </button>
  </section>

  <section appUiPanelCard class="grid gap-4">
    <app-ui-section-header
      label="Organizações vinculadas"
      help="Organizações associadas à sua conta de usuário."
    >
      <a sectionActions appButton variant="secondary" size="sm" routerLink="/onboarding" [queryParams]="{ mode: 'additional' }">
        <ng-icon name="heroPlus" size="16" aria-hidden="true" /> Criar empresa
      </a>
    </app-ui-section-header>

    <div appUiDataTableShell>
      <table appUiDataTable>
        <thead appUiDataTableHead>
          <tr>
            <th appUiDataTableHeaderCell class="w-[46%]">Organização</th>
            <th appUiDataTableHeaderCell class="w-[24%]">Acesso</th>
            <th appUiDataTableHeaderCell class="w-[30%] text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          @for (business of me()?.businesses ?? []; track business.id) {
            <tr appUiDataTableRow>
              <td appUiDataTableCell>
                <div class="flex min-w-0 items-center gap-2">
                  <span appUiRecordRowIcon class="shrink-0">
                    <ng-icon name="heroBuildingOffice2" size="16" aria-hidden="true" class="text-muted" />
                  </span>
                  <span class="block truncate font-semibold text-ink">{{ business.name }}</span>
                </div>
              </td>
              <td appUiDataTableCell>
                @if (business.membership) {
                  <span appBadge [tone]="business.id === currentBusiness()?.id ? 'accent' : business.membership.status === 'active' ? 'success' : 'neutral'" size="xs">
                    {{ business.id === currentBusiness()?.id ? 'Atual' : business.membership.status === 'active' ? 'Disponível' : 'Sem acesso ativo' }}
                  </span>
                } @else {
                  <span class="text-sm text-muted">Sem vínculo</span>
                }
              </td>
              <td appUiDataTableCell>
                <div appUiDataTableActions>
                  <button
                    appButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    (click)="switchBusiness(business)"
                    [disabled]="!canSwitchBusiness(business)"
                    [attr.title]="business.id === currentBusiness()?.id ? 'Este e o business atual.' : business.membership?.status === 'active' ? 'Entrar neste business' : 'Voce nao tem acesso ativo a este business.'"
                  >
                    <ng-icon name="heroBuildingOffice2" size="15" aria-hidden="true" />
                    {{ switchingBusinessId() === business.id ? 'Entrando' : business.id === currentBusiness()?.id ? 'Atual' : 'Entrar' }}
                  </button>
                  <button
                    appButton
                    type="button"
                    variant="danger"
                    size="sm"
                    (click)="disassociateBusiness(business)"
                    [disabled]="!business.membership || business.id === currentBusiness()?.id || switchingBusinessId() === business.id"
                    [attr.title]="business.id === currentBusiness()?.id ? 'Alterne para outro business antes de remover este vinculo.' : 'Remover vinculo com business'"
                  >
                    <ng-icon name="heroArrowRightOnRectangle" size="15" aria-hidden="true" />
                    Desassociar
                  </button>
                </div>
              </td>
            </tr>
          } @empty {
            <tr>
              <td appUiDataTableEmptyCell colspan="3">
                <app-ui-empty-state
                  compact
                  title="Nenhuma organização vinculada"
                  description="Organizações disponíveis para o seu usuário aparecerão aqui."
                />
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </section>
</div>
